import useRequest from "@/lib/request/useRequest";
import SimpleLayer from "./SimpleLayer";
import type { Selection } from "@heroui/react";

import { Button, Select, SelectItem } from "@heroui/react";
import { useCallback, useEffect, useRef, useState } from "react";
import useMap from "@/lib/map/useMap";
import {
    addUpdateGeojsonSource,
    addUpdateGeojsonLayers,
} from "@/lib/utils/map";
import {
    INSIGHTS_PLOTS_FILL_LAYER,
    INSIGHTS_PLOTS_SOURCE,
    INSIGHTS_PLOTS_SYMBOL_LAYER,
    SENTINEL_PLOTS_FILL_LAYER,
    SENTINEL_PLOTS_SOURCE,
} from "@/lib/map/layers";
import useFieldDashboard from "@/lib/fieldDashboard/useFieldDashboard";
import { Rx, ListRx } from "@/types/rx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload } from "@fortawesome/free-solid-svg-icons";
import { Plot } from "@/types/plot";
import { FeatureCollection, MultiPolygon } from "geojson";

function RxLayers() {
    const { fieldId, fieldQuery } = useFieldDashboard() ?? {};
    const { useGetRequest, downloadFile } = useRequest();
    const { map, mapLoaded } = useMap();

    const [selectedId, setSelectedId] = useState<Selection>(new Set());
    const prescribedPlotsVisibleRef = useRef(false);

    const insightsPlots = useGetRequest<FeatureCollection<MultiPolygon, Plot>>({
        url: `fields/${fieldId}/insights_plots/`,
        params: {
            boundary: "analytic",
        },
    });

    const establishmentPrescriptions = useGetRequest<ListRx[]>({
        url: `fields/${fieldId}/prescriptions/`,
        params: {
            type: "establishment",
        },
    });

    const rxDetails = useGetRequest<Rx>({
        url: `prescriptions/${selectedId != "all" ? selectedId.values().next().value : "all"}/`,
        enabled: selectedId != "all" ? selectedId.size > 0 : false,
    });

    useEffect(() => {
        if (!map || !mapLoaded || !rxDetails.data) return;

        addUpdateGeojsonSource(map, SENTINEL_PLOTS_SOURCE, {
            type: "geojson",
            data: rxDetails?.data?.plots,
            tolerance: 0,
        });

        addUpdateGeojsonLayers(map, [
            {
                ...SENTINEL_PLOTS_FILL_LAYER,
                layout: {
                    ...SENTINEL_PLOTS_FILL_LAYER.layout,
                    visibility: prescribedPlotsVisibleRef.current
                        ? "visible"
                        : "none",
                },
            },
        ]);
    }, [map, mapLoaded, rxDetails.data]);

    useEffect(() => {
        if (!map || !mapLoaded || !insightsPlots?.data) return;

        addUpdateGeojsonSource(map, INSIGHTS_PLOTS_SOURCE, {
            type: "geojson",
            data: insightsPlots.data,
            tolerance: 0,
        });

        addUpdateGeojsonLayers(map, [
            INSIGHTS_PLOTS_FILL_LAYER,
            INSIGHTS_PLOTS_SYMBOL_LAYER,
        ]);
    }, [insightsPlots.data, map, mapLoaded]);

    const onDownloadRx = useCallback(async () => {
        await downloadFile(
            `prescriptions/${rxDetails.data?.id}/plots/`,
            `${rxDetails.data?.name} Plots`,
        );
    }, [downloadFile, rxDetails.data?.id, rxDetails.data?.name]);

    const onDownloadInsightsPlots = useCallback(async () => {
        await downloadFile(
            `fields/${fieldId}/insights_plots/download/`,
            `${fieldQuery?.data?.name} Insights Plots`,
        );
    }, [downloadFile, fieldId, fieldQuery?.data?.name]);

    return (
        <>
            <SimpleLayer
                name="Prescribed Plots"
                layerNames={[SENTINEL_PLOTS_FILL_LAYER.id]}
                onValueChange={(isVisible) => {
                    prescribedPlotsVisibleRef.current = isVisible;
                }}
            >
                {() => (
                    <div className="flex flex-row mt-2 gap-2 items-center">
                        <Select
                            aria-label="Select Rx Prescription"
                            placeholder="Select Rx Prescription"
                            className="flex-1 min-w-0"
                            selectedKeys={selectedId}
                            size="sm"
                            onSelectionChange={setSelectedId}
                        >
                            {(establishmentPrescriptions.data ?? []).map(
                                (prescription) => {
                                    return (
                                        <SelectItem key={prescription.id}>
                                            {prescription.name}
                                        </SelectItem>
                                    );
                                },
                            )}
                        </Select>
                        <Button
                            color="primary"
                            variant="light"
                            size="sm"
                            isIconOnly
                            onPress={onDownloadRx}
                            isDisabled={!rxDetails.data}
                            className="flex-shrink-0"
                        >
                            <FontAwesomeIcon icon={faDownload} />
                        </Button>
                    </div>
                )}
            </SimpleLayer>

            <SimpleLayer
                name="Sentinel Plots"
                layerNames={[
                    INSIGHTS_PLOTS_FILL_LAYER.id,
                    INSIGHTS_PLOTS_SYMBOL_LAYER.id,
                ]}
                onDownload={onDownloadInsightsPlots}
                disabled={!insightsPlots?.data?.features?.length}
            ></SimpleLayer>
        </>
    );
}

export default RxLayers;
